<script setup lang="ts">
// アクションパネル: 記録 / 再生 / 削除 / JSON 入出力 / 一括適用
import { ref } from 'vue'
import { useActionsStore } from '@/automation/actions'
import { useDocumentStore } from '@/stores/documentStore'
import { useI18n } from '@/i18n'
import { runCommand } from '@/commands/registry'
import { openFiles } from '@/app/actions'
import { downloadBlob, pickFiles } from '@/io/download'
import { toast } from '@/composables/useToast'

const store = useActionsStore()
const docStore = useDocumentStore()
const { t } = useI18n()
const expanded = ref<string | null>(null)

function toggleRecord() {
  if (store.recording) store.stopRecording()
  else {
    const name = prompt(t('actions.newName'), `${t('actions.newName')} ${store.actions.length + 1}`)
    if (name === null) return
    store.startRecording(name || undefined)
  }
}
function rename(id: string, cur: string) {
  const n = prompt(t('actions.rename'), cur)
  if (n && n !== cur) store.rename(id, n)
}
function exportAll() {
  downloadBlob(new Blob([store.exportJson()], { type: 'application/json' }), 'actions.json')
}
async function importFile() {
  const files = await pickFiles('.json,application/json')
  for (const f of files) {
    try {
      const n = store.importJson(await f.text())
      toast.success(t('actions.imported', { n }))
    } catch (e) {
      toast.warn(e instanceof Error ? e.message : String(e))
    }
  }
}
/** 複数の画像ファイルにアクションを適用して PNG で書き出す */
async function batch(id: string) {
  const files = await pickFiles('image/*,.psd', true)
  if (!files.length) return
  if (docStore.isDirty && !confirm(t('dialog.close.confirm'))) return
  let n = 0
  for (const f of files) {
    docStore.closeDocument()
    await openFiles([f])
    if (!docStore.hasDocument) continue
    try {
      await store.play(id)
      await runCommand('document.export', { format: 'png' })
      n++
    } catch {
      break
    }
  }
  docStore.closeDocument()
  toast.success(t('actions.batchDone', { n }))
}
</script>

<template>
  <div class="panel">
    <div class="panel-title row">
      <span>{{ t('panel.actions') }}</span>
      <span class="spacer" />
      <button class="icon ghost" :class="{ rec: store.recording }" :title="store.recording ? t('actions.stop') : t('actions.record')" @click="toggleRecord">{{ store.recording ? '■' : '●' }}</button>
      <button class="icon ghost" :title="t('actions.import')" @click="importFile">⤓</button>
      <button class="icon ghost" :title="t('actions.export')" :disabled="!store.actions.length" @click="exportAll">⤒</button>
    </div>
    <div v-if="store.recording" class="recording">{{ t('actions.recording', { name: store.recording.name, n: store.recording.steps.length }) }}</div>
    <div class="list">
      <div v-if="!store.actions.length && !store.recording" class="empty muted">{{ t('actions.empty') }}</div>
      <div v-for="a in store.actions" :key="a.id" class="action">
        <div class="head" @dblclick="rename(a.id, a.name)">
          <button class="ghost tiny" @click="expanded = expanded === a.id ? null : a.id">{{ expanded === a.id ? '▾' : '▸' }}</button>
          <span class="name">{{ a.name }}</span>
          <span class="muted count">{{ t('actions.steps', { n: a.steps.length }) }}</span>
          <button class="ghost tiny" :title="t('actions.play')" :disabled="store.playing || !docStore.hasDocument" @click="store.play(a.id)">▶</button>
          <button class="ghost tiny" :title="t('actions.batch')" :disabled="store.playing" @click="batch(a.id)">⧉</button>
          <button class="ghost tiny" :title="t('actions.delete')" @click="store.remove(a.id)">🗑</button>
        </div>
        <ol v-if="expanded === a.id" class="steps">
          <li v-for="(s, i) in a.steps" :key="i"><code>{{ s.command }}</code><span class="muted params">{{ JSON.stringify(s.params) }}</span><button class="ghost tiny" @click="store.removeStep(a.id, i)">×</button></li>
        </ol>
      </div>
    </div>
  </div>
</template>

<style scoped>
.panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.list {
  overflow: auto;
}
.empty {
  padding: 8px 10px;
  font-size: 11px;
}
.recording {
  padding: 3px 10px;
  font-size: 11px;
  color: #e5484d;
}
.rec {
  color: #e5484d;
}
.action {
  border-bottom: 1px solid var(--line-weak);
}
.head {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
}
.name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.count {
  font-size: 10px;
}
.tiny {
  width: 22px;
  height: 20px;
  padding: 0;
}
.steps {
  margin: 0;
  padding: 0 8px 4px 28px;
  font-size: 11px;
}
.steps li {
  display: flex;
  gap: 6px;
  align-items: center;
}
.params {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 10px;
}
</style>
